import React from 'react'
import clsx from 'clsx'
import NextHead from 'next/head'
import { useRouter } from 'next/router'
import { useInView } from 'react-intersection-observer'

import { Icon } from '@wartek-id/icon'
import { Tabs, Tab, TabList, TabPanels, TabPanel } from '@wartek-id/tabs'

import { LoadingState } from 'app/ProfileRevampPage/components/LoadingState'
import PageHeader from 'app/ProfileRevampPage/components/PageHeader'
import ProfileHeader from 'app/ProfileRevampPage/components/ProfileHeader'
import Notification from 'app/ProfileRevampPage/components/Notification'
import SectionKarya from 'app/ProfileRevampPage/components/SectionKarya'
import SectionCertificate from 'app/ProfileRevampPage/components/SectionCertificate'
import SectionInfo from 'app/ProfileRevampPage/components/SectionInfo'
import SectionPersonalData from 'app/ProfileRevampPage/components/SectionPersonalData'
import { useProfile } from 'app/ProfileRevampPage/hooks/useProfile'
import { useSETrackEvent } from 'app/ProfileRevampPage/hooks/useSETrackEvent'
import {
  useProfileLayout,
  useProfileCertificateOnboarding,
} from 'stores/profile'
import { useAuth } from 'utils/auth'

import type { TProfileTabsType } from 'types/profile'

const ProfileRevampPage = () => {
  const [showPageHeaderContent, setShowPageHeaderContent] =
    React.useState<boolean>(false)
  const { SETrackPageLoaded, SETrackClickProfileTab } = useSETrackEvent()
  const [profileHeaderRef, profileHeaderIsInView] = useInView()
  const { isCurrentUser, profileData, profileFetchStatus } = useProfile()
  const { session } = useAuth()
  const router = useRouter()
  const profileLayoutStore = useProfileLayout()
  const profileCertificateOnboardingStore = useProfileCertificateOnboarding()

  const showCertificateTab = React.useMemo(() => Boolean(session), [session])
  const showPersonalDataTab = React.useMemo(
    () => isCurrentUser,
    [isCurrentUser]
  )

  React.useEffect(() => {
    if (profileFetchStatus === 'success') {
      setShowPageHeaderContent(!profileHeaderIsInView)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileHeaderIsInView])

  React.useEffect(() => {
    if (profileFetchStatus === 'success' && router?.query) {
      SETrackPageLoaded()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profileFetchStatus, router?.query])

  React.useEffect(() => {
    if (profileCertificateOnboardingStore.isRendered) {
      profileCertificateOnboardingStore.setFinished()
    }
    profileLayoutStore.setPageElement('KARYA')
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  return (
    <main className="relative flex flex-col h-screen max-w-xl mx-auto">
      <NextHead>
        <title>Profil Guru</title>
        <meta name="description" content="Kunjungi profil milik rekan guru" />
      </NextHead>

      <PageHeader
        title={profileData?.name}
        subtitle={profileData?.jenisPtk}
        showPageHeaderContent={showPageHeaderContent}
      />

      <div className="mt-[54px]">
        {['idle', 'loading'].includes(profileFetchStatus) ? (
          <LoadingState />
        ) : (
          <>
            {isCurrentUser ? (
              <Notification data-testid="profile-notification-update-duration">
                Harap tunggu 24 jam untuk melihat aktivitas terbaru Anda muncul
                di profil.
              </Notification>
            ) : null}

            <ProfileHeader ref={profileHeaderRef} />

            <div>
              <Tabs
                onChange={(index) => {
                  const pageElementTypes = [
                    'KARYA',
                    ...(showCertificateTab ? ['SERTIFIKAT'] : []),
                    'INFO',
                    ...(showPersonalDataTab ? ['DATA_PRIBADI'] : []),
                  ]
                  const pageElement = pageElementTypes[
                    index
                  ] as TProfileTabsType
                  profileLayoutStore.setPageElement(pageElement as string)
                  SETrackClickProfileTab({
                    pageElement,
                  })
                }}
              >
                <TabList
                  className={clsx(
                    'bg-white sticky top-[52px] z-10 shadow-sm w-full overflow-x-auto justify-around',
                    showCertificateTab &&
                      showPersonalDataTab &&
                      '!justify-between'
                  )}
                >
                  <Tab
                    className={clsx(
                      '!px-4 focus:!shadow-none !text-subdued',
                      profileLayoutStore.pageElement === 'KARYA' &&
                        '!text-blue-60'
                    )}
                  >
                    Karya
                  </Tab>
                  {showCertificateTab && (
                    <Tab
                      className={clsx(
                        '!px-4 focus:!shadow-none !text-subdued',
                        profileLayoutStore.pageElement === 'SERTIFIKAT' &&
                          '!text-blue-60'
                      )}
                    >
                      <div className="flex flex-wrap justify-center gap-x-1">
                        Sertifikat
                      </div>
                    </Tab>
                  )}
                  <Tab
                    className={clsx(
                      '!px-4 focus:!shadow-none !text-subdued',
                      profileLayoutStore.pageElement === 'INFO' &&
                        '!text-blue-60'
                    )}
                  >
                    Info
                  </Tab>
                  {showPersonalDataTab && (
                    <Tab
                      className={clsx(
                        '!px-4 focus:!shadow-none !text-subdued',
                        profileLayoutStore.pageElement === 'DATA_PRIBADI' &&
                          '!text-blue-60'
                      )}
                    >
                      <div className="flex flex-wrap justify-center gap-x-1">
                        Data pribadi{' '}
                        <Icon
                          fontSize="small"
                          className={clsx(
                            '!text-subdued',
                            profileLayoutStore.pageElement === 'DATA_PRIBADI' &&
                              '!text-blue-60'
                          )}
                        >
                          lock
                        </Icon>
                      </div>
                    </Tab>
                  )}
                </TabList>
                <TabPanels>
                  <TabPanel>
                    <SectionKarya />
                  </TabPanel>
                  {showCertificateTab && (
                    <TabPanel>
                      <SectionCertificate />
                    </TabPanel>
                  )}
                  <TabPanel>
                    <SectionInfo />
                  </TabPanel>
                  {showPersonalDataTab && (
                    <TabPanel>
                      <SectionPersonalData />
                    </TabPanel>
                  )}
                </TabPanels>
              </Tabs>
            </div>
          </>
        )}
      </div>
    </main>
  )
}

export default ProfileRevampPage
